import React, { useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import { useNeighborData } from './NeighborDataContext';
import { useOptimizeScope } from './OptimizeScopeContext';
import { filterFreshnessToScope } from '../../utils/optimizeScopeUtils';
import { NeighborDataChip } from './NeighborDataStatus';

// NeighborDataChipConnected reads the shared NeighborDataContext and
// renders the compact status pill + details popover. Every analyser
// panel drops this into its AnalyzerHeader right-content slot so the
// chip is identical across tabs.
const NeighborDataChipConnected = () => {
  const {
    freshness,
    devices,
    loading,
    refresh,
    scanDevices,
    scanAll,
    scanningSerials,
  } = useNeighborData();
  const { scopeSerials, isSiteWide, scopeLabel } = useOptimizeScope();
  const { enqueueSnackbar } = useSnackbar();

  const displayFreshness = useMemo(
    () => (isSiteWide ? freshness : filterFreshnessToScope(freshness, devices, scopeSerials)),
    [freshness, devices, scopeSerials, isSiteWide],
  );

  const missingSerials = useMemo(
    () => displayFreshness?.missing_serials || [],
    [displayFreshness?.missing_serials],
  );
  const staleSerials = useMemo(
    () => displayFreshness?.stale_serials || [],
    [displayFreshness?.stale_serials],
  );

  const handleScanMissing = useCallback(async () => {
    if (!missingSerials.length) return;
    const { accepted, failed } = await scanDevices(missingSerials);
    enqueueSnackbar(
      failed === 0
        ? `Triggered neighbour scan for ${accepted} device(s). Scans take a few minutes to complete.`
        : `Triggered ${accepted} scan(s); ${failed} failed to start.`,
      { variant: failed === 0 ? 'info' : 'warning' },
    );
  }, [missingSerials, scanDevices, enqueueSnackbar]);

  const handleScanAll = useCallback(async () => {
    const { accepted } = await scanAll();
    enqueueSnackbar(
      accepted
        ? 'Triggered neighbour scan for all connected devices. Scans take a few minutes to complete.'
        : 'Failed to trigger neighbour scan.',
      { variant: accepted ? 'info' : 'error' },
    );
  }, [scanAll, enqueueSnackbar]);

  return (
    <NeighborDataChip
      freshness={displayFreshness}
      loading={loading}
      onRefresh={refresh}
      onScanMissing={handleScanMissing}
      onScanAll={handleScanAll}
      scanningSerials={scanningSerials}
      missingSerials={missingSerials}
      staleSerials={staleSerials}
      oldestSerial={displayFreshness?.oldest_serial}
      oldestAgeS={displayFreshness?.oldest_age_s}
      staleThresholdS={displayFreshness?.stale_threshold_s}
      scopeLabel={isSiteWide ? null : scopeLabel}
    />
  );
};

export default NeighborDataChipConnected;
